import { ApolloClient, InMemoryCache, createHttpLink, from, Observable } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { auth } from './auth'
import { CLAIM_CONFLICT_PREFIX } from './delivery'
import toast from 'react-hot-toast'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/graphql'

// Delivery-route READ documents (graphql/delivery-run.ts). Against an API that has not
// deployed routes yet they fail validation; each of these screens already says "Routes are
// not available right now" inline, and they poll, so that failure is not toasted.
const ROUTE_READ_OPERATIONS = new Set(['DeliveryRuns', 'MyDeliveryRuns', 'DeliveryDrivers', 'GetOrderDeliveryRun'])

const httpLink = createHttpLink({
  uri: API_URL,
})

const authLink = setContext((_, { headers }) => {
  const token = auth.getToken()
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    }
  }
})

// Shared promise so concurrent 401s trigger only one refresh call
let refreshPromise: Promise<boolean> | null = null

// Uses a plain fetch so the request bypasses authLink entirely.
// authLink would attach the expired access token as a Bearer header, which
// causes the backend's auth middleware to reject the request before the
// refreshToken resolver can read input.refreshToken from the body.
async function getNewTokens(): Promise<boolean> {
  const refreshToken = auth.getRefreshToken()
  if (!refreshToken) return false

  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: `
          mutation RefreshToken($input: RefreshTokenInput!) {
            refreshToken(input: $input) {
              token
              refreshToken
            }
          }
        `,
        variables: { input: { refreshToken } },
      }),
    })

    const { data, errors } = await response.json()
    if (errors?.length || !data?.refreshToken?.token) return false

    auth.setTokens(data.refreshToken.token, data.refreshToken.refreshToken)
    return true
  } catch {
    return false
  }
}

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  // Routes not deployed on this API yet. Checked before BOTH toast paths: a validation
  // failure is an HTTP 400, which Apollo reports as a networkError too (graphQLErrors are
  // then read from its body), so skipping only the per-error toast would still fire "Network
  // error" on every poll — and wrongly blame the connection. Route MUTATIONS still toast:
  // they are an operator's own tap.
  // FORBIDDEN joins it since 2026-10-01: the board's routes panel now shows for every role
  // that can open the board, and an API with routes but without that read widening refuses
  // deliveryRuns for a non-planner on every poll — the panel already says so inline.
  if (
    ROUTE_READ_OPERATIONS.has(operation.operationName) &&
    graphQLErrors?.length &&
    graphQLErrors.every(
      e => e.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' || e.extensions?.code === 'FORBIDDEN'
    )
  ) {
    return
  }

  if (graphQLErrors) {
    const hasUnauth = graphQLErrors.some(e => e.extensions?.code === 'UNAUTHENTICATED')

    // Surface non-auth errors immediately
    graphQLErrors.forEach(({ message, extensions }) => {
      if (extensions?.code === 'UNAUTHENTICATED') return
      if (extensions?.code === 'FORBIDDEN') {
        toast.error('You do not have permission to perform this action.')
      } else if (message.startsWith(CLAIM_CONFLICT_PREFIX)) {
        // Legacy: no POS screen calls claimDelivery any more — the driver screen starts
        // planned routes instead — so this only guards an old client build still open on
        // a phone. It stays silent for the reason it always was: the message belonged
        // inline on the card it referred to.
        return
      } else {
        toast.error(message)
      }
    })

    if (hasUnauth) {
      // The refresh mutation itself got a 401 — session is truly dead
      if (operation.operationName === 'RefreshToken') {
        auth.clearAuth()
        window.location.href = '/login'
        toast.error('Session expired. Please login again.')
        return
      }

      return new Observable(observer => {
        if (!refreshPromise) {
          refreshPromise = getNewTokens().finally(() => {
            refreshPromise = null
          })
        }

        refreshPromise
          .then(success => {
            if (!success) {
              auth.clearAuth()
              window.location.href = '/login'
              toast.error('Session expired. Please login again.')
              observer.complete()
              return
            }

            // Inject the fresh token into this operation's context so authLink
            // doesn't have to wait for the next request to pick it up
            operation.setContext(({ headers = {} }: { headers: Record<string, string> }) => ({
              headers: {
                ...headers,
                authorization: `Bearer ${auth.getToken()}`,
              },
            }))

            forward(operation).subscribe({
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            })
          })
          .catch(() => {
            auth.clearAuth()
            window.location.href = '/login'
            toast.error('Session expired. Please login again.')
            observer.complete()
          })
      })
    }
  }

  if (networkError) {
    toast.error('Network error. Please check your connection.')
  }
})

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          users: {
            merge(_existing = [], incoming) {
              return incoming
            },
          },
          roles: {
            merge(_existing = [], incoming) {
              return incoming
            },
          },
          permissions: {
            merge(_existing = [], incoming) {
              return incoming
            },
          },
          products: {
            merge(_existing = [], incoming) {
              return incoming
            },
            keyArgs: ['filters']
          },
          categories: {
            merge(_existing = [], incoming) {
              return incoming
            },
          },
          // OrderStats has no id, so results store unnormalized keyed by args.
          // Two documents select this field (GET_ORDER_STATS and GET_DAILY_STATS);
          // with byte-identical variables (e.g. Financial Overview set to exactly
          // one calendar month = the Daily page's window) the default policy
          // REPLACES the cached object, dropping the other document's fields and
          // forcing refetch loops. merge:true deep-merges instead — safe because
          // both are views of the same server aggregate for the same args.
          orderStats: {
            merge: true,
          },
          // Same hazard as orderStats: multiple report documents select
          // productSalesStats with identical args but different selections
          // (byHourProduct vs byProduct vs topProducts).
          productSalesStats: {
            merge: true,
          },
          // And again: the reconciliation table selects a four-field slice of
          // productDayStats while the drill panel selects the full margin row.
          productDayStats: {
            merge: true,
          },
          // Same class of unnormalized report aggregate — one document today,
          // but the default replace-on-write policy would silently drop fields
          // the moment a second document selects a different slice.
          productVoidStats: {
            merge: true,
          },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
    },
    query: {
      errorPolicy: 'all',
    },
  },
})

